// More procedural sounds, for things the Godot bank never had: livestock, the extraction
// bell, saloon batwing doors, brass casings, wreck sparks, the whiskey bottle (uncork, gulps, the smash), and
// the outdoor reverb's impulse.
// Plain math into Float32Arrays, computed once at unlock (a few ms in total). Each generator
// takes a seed so one name can carry several variations for round-robin.

type Gen = (sr: number, seed: number) => Float32Array;

function rng(seed: number) {
  let s = (seed * 2654435761) >>> 0 || 1;
  return () => { s ^= s << 13; s ^= s >>> 17; s ^= s << 5; return ((s >>> 0) / 4294967296) * 2 - 1; };
}
function normalize(b: Float32Array, peak: number) {
  let m = 0;
  for (let i = 0; i < b.length; i++) m = Math.max(m, Math.abs(b[i]));
  if (m > 0) for (let i = 0; i < b.length; i++) b[i] *= peak / m;
  return b;
}
/** state-variable band-pass, returns a stepper */
function svf() {
  let low = 0, band = 0;
  return (x: number, fc: number, q: number, sr: number) => {
    const f = 2 * Math.sin((Math.PI * Math.min(fc, sr * 0.22)) / sr);
    low += f * band; const high = x - low - q * band; band += f * high;
    return band;
  };
}

/** A cow. A glottal pulse train (not a saw: the pulse has the reedy, nasal edge of a real
 *  moo) through three formants that open from a closed "mm" to a wide "oo-aa" and close
 *  again, a pitch arc that rises, holds and sags, breath noise, and a little vibrato and
 *  jitter so no two are alike. seed 0 cow, 1 bull (lower, longer, rougher), 2 short low. */
const moo: Gen = (sr, seed) => {
  const r = rng(seed + 3);
  const bull = seed === 1, short = seed === 2;
  const dur = bull ? 1.7 : short ? 0.85 : 1.35;
  const f0a = bull ? 78 : short ? 96 : 102, f0b = bull ? 104 : short ? 118 : 138;
  const n = Math.floor(sr * dur), b = new Float32Array(n);
  const F = [svf(), svf(), svf(), svf()];
  let ph = 0, jit = 0, breath = 0;
  for (let i = 0; i < n; i++) {
    const t = i / sr, k = t / dur;
    const arc = Math.sin(Math.PI * Math.min(1, k * 1.25)) ** 0.7;
    jit += 0.002 * (r() - jit);
    const f0 = (f0a + (f0b - f0a) * arc - 14 * k * k) * (1 + 0.012 * Math.sin(2 * Math.PI * 5.2 * t) + jit * 3);
    ph += f0 / sr;
    if (ph >= 1) ph -= 1;
    // Rosenberg-ish glottal pulse: open phase 0..0.6, closed after
    const g = ph < 0.6 ? Math.sin((Math.PI * ph) / 0.6) ** 2 : 0;
    const dg = ph < 0.6 ? Math.sin((2 * Math.PI * ph) / 0.6) : 0;
    breath += 0.4 * (r() - breath);
    const src = dg * 0.8 + g * 0.3 + breath * 0.08 * (0.4 + arc);
    const open = Math.sin(Math.PI * Math.min(1, k * 1.1)) ** 0.8; // mm -> oo-aa -> mm
    let v = F[0](src, 280 + 420 * open, 0.35, sr) * 1.0;
    v += F[1](src, 700 + 500 * open, 0.4, sr) * 0.55;
    v += F[2](src, 2300 + 300 * open, 0.6, sr) * 0.12;
    v += F[3](src, 220, 0.2, sr) * 0.5 * (1 - open); // nasal murmur while the mouth is shut
    if (bull) v = Math.tanh(v * 2.2) * 0.6;
    const env = Math.min(1, t * 10) * Math.min(1, (dur - t) * 4.5);
    b[i] = v * env;
  }
  return normalize(b, 0.8);
};

/** The extraction bell: a big brass hand bell struck hard. Inharmonic bell partials (hum,
 *  prime, tierce, quint, nominal...) each with its own decay, a slow beat on the hum, a hard
 *  clapper transient, and a slightly detuned second strike for seed 1. */
const bell: Gen = (sr, seed) => {
  const n = Math.floor(sr * 3.6), b = new Float32Array(n), r = rng(41 + seed);
  const f0 = seed ? 372 : 392;
  const parts = [[0.5, 0.55, 0.8], [1, 1, 1.3], [1.183, 0.6, 1.9], [1.506, 0.35, 2.5], [2, 0.5, 2.1], [2.514, 0.22, 4.2], [3.011, 0.18, 5.5], [4.166, 0.1, 8], [5.433, 0.06, 11]];
  let lp = 0;
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    let v = 0;
    for (const [m, a, d] of parts) v += Math.sin(2 * Math.PI * f0 * m * t + m * 1.7) * a * Math.exp(-t * d);
    v *= 1 + 0.1 * Math.sin(2 * Math.PI * 2.1 * t);
    lp += 0.55 * (r() - lp);
    v += lp * Math.exp(-t * 80) * 1.1 + Math.sin(2 * Math.PI * 3150 * t) * Math.exp(-t * 60) * 0.3;
    b[i] = v * Math.min(1, t * 3000);
  }
  return normalize(b, 0.9);
};

/** A brass casing hitting packed dirt or plank: two or three bounces of a short inharmonic
 *  ping. Different seed = different pitch, bounce count and spacing. */
const tink: Gen = (sr, seed) => {
  const r = rng(seed + 77);
  const n = Math.floor(sr * 0.5), b = new Float32Array(n);
  const f = 3900 + 1400 * (r() * 0.5 + 0.5);
  const hits = [0, 0.07 + 0.04 * (r() * 0.5 + 0.5), 0.16 + 0.05 * (r() * 0.5 + 0.5)];
  const amps = [1, 0.45, 0.18];
  for (let h = 0; h < 3; h++) {
    const s0 = Math.floor(hits[h] * sr);
    for (let i = s0; i < n; i++) {
      const t = (i - s0) / sr;
      const e = Math.exp(-t * 55);
      if (e < 0.001) break;
      b[i] += amps[h] * e * (Math.sin(2 * Math.PI * f * t) + 0.5 * Math.sin(2 * Math.PI * f * 2.76 * t) + 0.25 * Math.sin(2 * Math.PI * f * 5.4 * t));
      b[i] += amps[h] * Math.exp(-t * 900) * r() * 0.6;
    }
  }
  return normalize(b, 0.55);
};

/** Sparks off a wreck: a crackle of random short electric pops over a fizzing hiss. */
const fizz: Gen = (sr, seed) => {
  const r = rng(seed + 5);
  const n = Math.floor(sr * 0.8), b = new Float32Array(n);
  const hp = svf();
  let pop = 0;
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    if (r() > 0.9975 - 0.002 * Math.exp(-t * 6)) pop = 1;
    pop *= 0.985;
    const hiss = hp(r(), 5200, 0.5, sr) * 0.25 * Math.exp(-t * 3.5);
    b[i] = hiss + pop * r() * 0.9;
  }
  return normalize(b, 0.6);
};

/** Saloon batwing doors swinging: a dry wooden creak (a stick-slip pulse train sweeping in
 *  pitch through a wooden body resonance), the slap of the leaves meeting, and a softer
 *  return swing. */
const door: Gen = (sr, seed) => {
  const r = rng(seed + 9);
  const n = Math.floor(sr * 1.3), b = new Float32Array(n);
  const body = svf(), body2 = svf();
  let ph = 0;
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    const creakOn = t < 0.55 ? Math.sin(Math.PI * (t / 0.55)) : 0;
    const rate = 38 + 90 * (t / 0.55) + 15 * r();
    ph += rate / sr;
    let x = 0;
    if (ph >= 1) { ph -= 1; x = 1; }
    let v = body(x * creakOn, 900 + 300 * Math.sin(t * 9), 0.15, sr) * 1.4 + body2(x * creakOn, 2300, 0.25, sr) * 0.5;
    // the slap (leaves meet) and the return knock
    for (const [at, a] of [[0.58, 1], [0.92, 0.5], [1.12, 0.22]] as const) {
      const dt = t - at;
      if (dt >= 0 && dt < 0.12) v += a * Math.exp(-dt * 60) * (Math.sin(2 * Math.PI * 180 * dt) * 0.8 + r() * 0.5);
    }
    b[i] = v;
  }
  return normalize(b, 0.75);
};

/** A glass bottle smashing on hard ground (the whiskey toss): a bright crack, a low knock of the thick base, a
 *  shower of inharmonic tinkles from the pieces, a few late skitters as they settle, and the wet slap of the dregs. */
const shatter: Gen = (sr, seed) => {
  const r = rng(301 + seed), n = Math.floor(sr * 0.9), b = new Float32Array(n);
  const bp = svf(), bp2 = svf();
  let lp = 0;
  // the shards: 34 short rings, most in the first 120 ms, thinning out
  const rings: [number, number, number, number][] = [];
  for (let k = 0; k < 34; k++) {
    const at = Math.pow(Math.abs(r()), 2.2) * 0.42 + (k < 6 ? 0 : 0.004);
    rings.push([at, 2200 + Math.abs(r()) * 6800, 0.25 + Math.abs(r()) * 0.75 * Math.exp(-at * 5), 28 + Math.abs(r()) * 50]);
  }
  for (let k = 0; k < 4; k++) rings.push([0.45 + Math.abs(r()) * 0.3, 3000 + Math.abs(r()) * 4000, 0.18, 60]); // skitters
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    const x = r();
    // the crack: wide noise, hard attack, gone in ~25 ms
    let v = bp(x, 4200, 0.6, sr) * Math.exp(-t * 140) * 1.4;
    // the base knocking the ground
    v += Math.sin(2 * Math.PI * (210 - 60 * t) * t) * Math.exp(-t * 45) * 0.55;
    // the dregs: low wet noise, a little later
    lp += 0.06 * (x - lp);
    const wt = t - 0.012;
    if (wt > 0) v += bp2(lp, 700, 1.2, sr) * Math.exp(-wt * 22) * 2.2;
    for (const [at, f, a, d] of rings) {
      const dt = t - at;
      if (dt < 0 || dt > 0.12) continue;
      v += a * Math.exp(-dt * d) * Math.sin(2 * Math.PI * f * dt) * 0.42 * Math.min(1, dt * 3000);
    }
    b[i] = v;
  }
  return normalize(b, 0.82);
};

/** One swallow: a throaty glug (a falling low tone through the throat's formant) with a wet click on top. */
const gulp: Gen = (sr, seed) => {
  const r = rng(411 + seed), n = Math.floor(sr * 0.22), b = new Float32Array(n);
  const bp = svf();
  let ph = 0, lp = 0;
  const f0 = 165 + seed * 12;
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    const f = f0 * (1 - 0.45 * Math.min(1, t / 0.09));
    ph += (2 * Math.PI * f) / sr;
    const env = Math.min(1, t * 180) * Math.exp(-t * 18);
    lp += 0.1 * (r() - lp);
    const tone = Math.sin(ph) + 0.35 * Math.sin(ph * 2.1);
    let v = bp(tone + lp * 2, 420 + 300 * Math.exp(-t * 30), 0.9, sr) * env * 1.6;
    v += r() * Math.exp(-t * 600) * 0.35; // the click of the tongue
    b[i] = v;
  }
  return normalize(b, 0.6);
};

/** The cork out: a tight hollow pop (a short resonant sweep up the neck) and a faint squeak before it. */
const uncork: Gen = (sr, seed) => {
  const r = rng(503 + seed), n = Math.floor(sr * 0.18), b = new Float32Array(n);
  const bp = svf(), sq = svf();
  let ph = 0;
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    const sqT = t < 0.035 ? sq(r(), 2600 + 900 * t / 0.035, 0.08, sr) * 0.18 * Math.sin(Math.PI * t / 0.035) : 0;
    const pt = t - 0.035;
    let v = sqT;
    if (pt > 0) {
      ph += (2 * Math.PI * (380 + 900 * Math.exp(-pt * 60))) / sr;
      v += Math.sin(ph) * Math.exp(-pt * 55) * 0.9 + bp(r(), 1300, 0.5, sr) * Math.exp(-pt * 220) * 1.2;
    }
    b[i] = v;
  }
  return normalize(b, 0.7);
};

/** Outdoor reverb impulse: no walls, just a few far slapbacks off the mesas and a dark,
 *  short diffuse tail. Stereo, decorrelated. */
export function outdoorIR(ctx: BaseAudioContext): AudioBuffer {
  const sr = ctx.sampleRate, n = Math.floor(sr * 1.6);
  const buf = ctx.createBuffer(2, n, sr);
  for (let c = 0; c < 2; c++) {
    const d = buf.getChannelData(c), r = rng(101 + c * 17);
    let lp = 0;
    for (let i = 0; i < n; i++) {
      const t = i / sr;
      lp += 0.18 * (r() - lp); // dark diffuse
      d[i] = lp * Math.exp(-t * 4.2) * Math.min(1, t * 60) * 0.5;
    }
    for (const [at, a] of [[0.045 + c * 0.011, 0.4], [0.13 + c * 0.02, 0.28], [0.31 - c * 0.03, 0.2], [0.52 + c * 0.04, 0.12]]) {
      const s = Math.floor(at * sr);
      for (let k = 0; k < 240 && s + k < n; k++) d[s + k] += a * Math.exp(-k / 40) * r();
    }
  }
  return buf;
}

/** name -> [generator, seeds]: one variation per seed. */
export const SYNTH_MORE: Record<string, [Gen, number[]]> = {
  cow_moo: [moo, [0, 2]],
  bull_moo: [moo, [1]],
  hand_bell: [bell, [0, 1]],
  shell_tink: [tink, [0, 1, 2, 3]],
  wreck_fizz: [fizz, [0, 1]],
  door_swing: [door, [0, 1]],
  glass_shatter: [shatter, [0, 1, 2]],
  whiskey_gulp: [gulp, [0, 1, 2]],
  whiskey_uncork: [uncork, [0, 1]],
};

export function synthMore(ctx: BaseAudioContext, name: string): AudioBuffer[] {
  const e = SYNTH_MORE[name];
  if (!e) return [];
  const out: AudioBuffer[] = [];
  for (const s of e[1]) {
    const data = e[0](ctx.sampleRate, s);
    const buf = ctx.createBuffer(1, data.length, ctx.sampleRate);
    buf.getChannelData(0).set(data);
    out.push(buf);
  }
  return out;
}
